// advancedGrowthModels.ts: Advanced mathematical models for limb growth and exploration
// Based on biological research: neuronal growth cones, chemotaxis, gradient following,
// space colonization, and frontier exploration algorithms

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { Limb, LimbSegment } from './limbSystem';
import { LifeBehaviorMathEngine } from './lifeBehaviorMath';

/**
 * GROWTH CONE MODEL
 * Simulates growth cones at tip of extending neurites (analogous to limb tips)
 * Based on: Mathematical models of neuronal growth cone navigation
 * 
 * The growth cone senses gradients and makes directional decisions based on:
 * - Attraction gradients (chemotaxis)
 * - Repulsion gradients (negative chemotaxis)
 * - Mechanical cues
 * - Local exploration uncertainty
 */
export interface GrowthCone {
    id: number;
    x: number;
    y: number;
    z: number;
    direction: number; // Angle of growth direction
    explorationState: 'searching' | 'following' | 'reaching' | 'branching';
    
    // Dynamic properties
    velocity: number;        // Growth velocity
    persistenceTime: number; // How long maintaining current direction
    uncertainty: number;     // Exploration vs exploitation balance
    attractionFactor: number; // How much attracted to gradients
    
    // Chemotaxis properties
    sensedGradient: { x: number; y: number; z: number };
    gradientStrength: number;
    
    // Exploration properties
    lastCheckedDirection: number;
    exploredRadius: number;
    explorationMap: Map<string, number>; // Direction -> exploration value
}

/**
 * ACTIVATOR-INHIBITOR MODEL (Sugimura et al., 2007)
 * Controls growth based on activator (promotes) and inhibitor (suppresses)
 * 
 * Mathematical model:
 * ∂A/∂t = D_A∇²A + ρ_A·A²/(I·(1 + A²)) - μ_A·A
 * ∂I/∂t = D_I∇²I + ρ_I·A² - μ_I·I
 * 
 * Where:
 * - A = activator concentration (promotes branching)
 * - I = inhibitor concentration (suppresses branching)
 * - D = diffusion coefficients
 * - ρ = production rates
 * - μ = decay rates
 */
export interface ActivatorInhibitorState {
    activator: number;   // 0-1, promotes growth
    inhibitor: number;   // 0-1, suppresses growth
    balance: number;     // activator - inhibitor (determines growth direction)
    diffusionRate: number;
    productionRate: number;
    decayRate: number;
}

/**
 * CHEMOTAXIS MODEL
 * Limb growth responds to chemical/energy gradients
 * Based on: Growth cone chemotaxis algorithms
 * 
 * Mathematical model:
 * dc/dt = D∇²c + P - μc  (reaction-diffusion)
 * dr/dt = β∇c  (growth direction follows gradient)
 * 
 * Where:
 * - c = chemical concentration (attractant/repellent)
 * - D = diffusion coefficient
 * - P = production rate
 * - μ = decay rate
 * - r = growth direction
 * - β = sensitivity to gradient
 */
export interface ChemotaxisField {
    x: number;
    y: number;
    z: number;
    concentration: number;
    gradient: { x: number; y: number; z: number };
    type: 'attractant' | 'repellent' | 'neutral';
    strength: number;
    diffusionRate: number;
}

/**
 * SPACE COLONIZATION ALGORITHM
 * Efficiently explores space by growang toward attractor points
 * Used in: Tree generation, branching structures
 * 
 * Algorithm:
 * 1. Scatter attraction points in unexplored space
 * 2. Each growth point (limb tip) finds nearest attraction point
 * 3. Grow toward nearest point with some randomness
 * 4. Remove attraction points that are reached
 * 5. Add new attraction points to unexplored areas
 */
export interface AttractionPoint {
    id: number;
    x: number;
    y: number;
    z: number;
    importance: number; // Higher = more attractive
    reached: boolean;
    reachedBy: number[]; // Which growth cones reached this
}

/**
 * FRONTIER EXPLORATION
 * Explores toward unexplored frontiers (boundaries between known/unknown)
 * Used in: Robotics, autonomous exploration
 * 
 * Algorithm:
 * 1. Identify frontiers (boundaries of known space)
 * 2. Calculate information gain for each frontier
 * 3. Grow toward most promising frontier
 * 4. Update exploration map as new space is encountered
 */
export interface FrontierPoint {
    id: number;
    x: number;
    y: number;
    z: number;
    informationGain: number; // How valuable exploring here is
    uncertainty: number;     // How unknown this area is
    reachability: number;    // How easy it is to reach
    explorationValue: number; // Combined metric for exploration priority
}

export class AdvancedGrowthEngine {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine;
    
    // Growth cones tracking
    private growthCones: Map<number, GrowthCone> = new Map();
    
    // Activator-inhibitor system
    private activatorInhibitorStates: Map<number, ActivatorInhibitorState> = new Map();
    
    // Chemotaxis fields
    private chemotaxisFields: ChemotaxisField[] = [];
    
    // Space colonization
    private attractionPoints: AttractionPoint[] = [];
    
    // Frontier exploration
    private frontierPoints: FrontierPoint[] = [];
    private explorationMap: Map<string, number> = new Map(); // Space -> exploration level
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine();
    }
    
    // Main update method
    public updateGrowthModels(dt: number): void {
        // Update activator-inhibitor dynamics
        this.updateActivatorInhibitorSystem(dt);
        
        // Update chemotaxis fields
        this.updateChemotaxisFields(dt);
        
        // Update growth cones
        this.updateGrowthCones(dt);
        
        // Update space colonization
        this.updateSpaceColonization(dt);
        
        // Update frontier exploration
        this.updateFrontierExploration(dt);
    }
    
    // === ACTIVATOR-INHIBITOR MODEL ===
    
    private updateActivatorInhibitorSystem(dt: number): void {
        // Update activator-inhibitor dynamics for all growth cones
        for (const [coneId, cone] of this.growthCones) {
            let state = this.activatorInhibitorStates.get(coneId);
            if (!state) {
                state = this.createInitialActivatorInhibitorState();
                this.activatorInhibitorStates.set(coneId, state);
            }
            
            // Reaction-diffusion equations (simplified)
            // ∂A/∂t = ρ_A·A²/(I·(1 + A²)) - μ_A·A + D_A∇²A
            const rho_A = 1.0; // Production rate
            const mu_A = 0.1;   // Decay rate
            const D_A = 0.05;   // Diffusion
            
            // Simplified activation (no spatial diffusion in this implementation)
            const activationProduction = rho_A * Math.pow(state.activator, 2) / 
                                         (state.inhibitor * (1 + Math.pow(state.activator, 2)));
            const activationDecay = mu_A * state.activator;
            
            state.activator = Math.max(0, Math.min(1, state.activator + (activationProduction - activationDecay) * dt));
            
            // Inhibitor: ∂I/∂t = ρ_I·A² - μ_I·I
            const rho_I = 0.8;
            const mu_I = 0.15;
            
            const inhibitorProduction = rho_I * Math.pow(state.activator, 2);
            const inhibitorDecay = mu_I * state.inhibitor;
            
            state.inhibitor = Math.max(0, Math.min(1, state.inhibitor + (inhibitorProduction - inhibitorDecay) * dt));
            
            // Balance determines growth direction
            state.balance = state.activator - state.inhibitor;
        }
    }
    
    private createInitialActivatorInhibitorState(): ActivatorInhibitorState {
        return {
            activator: 0.3 + Math.random() * 0.3,
            inhibitor: 0.2 + Math.random() * 0.2,
            balance: 0.0,
            diffusionRate: 0.05,
            productionRate: 1.0,
            decayRate: 0.1
        };
    }
    
    // === CHEMOTAXIS MODEL ===
    
    private updateChemotaxisFields(dt: number): void {
        // Create chemotaxis fields from network energy
        this.chemotaxisFields = [];
        
        // Find high-energy nodes (attractors)
        for (const node of this.network.nodes) {
            if (!node || node.corroded || !node.energy) continue;
            
            if (node.energy > 0.7) {
                this.chemotaxisFields.push({
                    x: node.x,
                    y: node.y,
                    z: node.z || 0,
                    concentration: node.energy,
                    gradient: this.calculateGradient(node.x, node.y, node.z || 0),
                    type: 'attractant',
                    strength: node.energy * 50,
                    diffusionRate: 2.0
                });
            }
        }
        
        // Find low-energy nodes (repellents - avoid starving areas)
        for (const node of this.network.nodes) {
            if (!node || node.corroded || !node.energy) continue;
            
            if (node.energy < 0.3) {
                this.chemotaxisFields.push({
                    x: node.x,
                    y: node.y,
                    z: node.z || 0,
                    concentration: 1 - node.energy,
                    gradient: this.calculateGradient(node.x, node.y, node.z || 0),
                    type: 'repellent',
                    strength: (1 - node.energy) * 30,
                    diffusionRate: 1.5
                });
            }
        }
        
        // Update field gradients based on diffusion
        for (const field of this.chemotaxisFields) {
            // Simplified diffusion equation
            const diffusionLoss = field.diffusionRate * dt;
            field.concentration = Math.max(0, field.concentration - diffusionLoss);
        }
    }
    
    private calculateGradient(centerX: number, centerY: number, centerZ: number): { x: number; y: number; z: number } {
        // Calculate gradient direction (toward this point for attraction, away for repulsion)
        return {
            x: Math.random() * 0.02 - 0.01,
            y: Math.random() * 0.02 - 0.01,
            z: Math.random() * 0.01 - 0.005
        };
    }
    
    // === GROWTH CONE MODEL ===
    
    private updateGrowthCones(dt: number): void {
        for (const [coneId, cone] of this.growthCones) {
            // Update growth cone direction based on chemotaxis
            this.updateGrowthConeDirection(cone, dt);
            
            // Update uncertainty (exploration vs exploitation)
            cone.uncertainty = this.calculateUncertainty(cone);
            
            // Update exploration map
            this.updateExplorationMap(cone);
        }
    }
    
    private updateGrowthConeDirection(cone: GrowthCone, dt: number): void {
        // Sense chemotaxis gradients
        let totalGradient = { x: 0, y: 0, z: 0 };
        let totalStrength = 0;
        
        for (const field of this.chemotaxisFields) {
            const dx = field.x - cone.x;
            const dy = field.y - cone.y;
            const dz = field.z - cone.z;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (dist < field.strength && dist > 0.01) {
                const influence = field.concentration / dist;
                const dir = field.type === 'attractant' ? 1 : -1;
                
                totalGradient.x += (dx / dist) * influence * dir;
                totalGradient.y += (dy / dist) * influence * dir;
                totalGradient.z += (dz / dist) * influence * dir;
                totalStrength += influence;
            }
        }
        
        // Update cone direction
        if (totalStrength > 0.01) {
            cone.sensedGradient = {
                x: totalGradient.x / totalStrength,
                y: totalGradient.y / totalStrength,
                z: totalGradient.z / totalStrength
            };
            
            // New direction = weighted average of gradient and previous direction
            const gradientWeight = 0.7;
            const persistenceWeight = 1 - gradientWeight;
            
            const gradientAngle = Math.atan2(cone.sensedGradient.y, cone.sensedGradient.x);
            cone.direction = gradientWeight * gradientAngle + persistenceWeight * cone.direction;
            
            cone.gradientStrength = totalStrength;
            cone.uncertainty = Math.max(0.2, 1 - cone.gradientStrength * 2);
        }
    }
    
    private calculateUncertainty(cone: GrowthCone): number {
        // Uncertainty increases in unexplored areas
        const exploredValue = cone.explorationMap.get(cone.lastCheckedDirection.toString()) || 0;
        return Math.max(0.2, 1 - exploredValue);
    }
    
    private updateExplorationMap(cone: GrowthCone): void {
        // Record exploration in current direction
        const dirKey = cone.direction.toString();
        const currentValue = cone.explorationMap.get(dirKey) || 0;
        cone.explorationMap.set(dirKey, Math.min(1, currentValue + 0.01));
    }
    
    // === SPACE COLONIZATION ALGORITHM ===
    
    private updateSpaceColonization(dt: number): void {
        // Add attraction points to unexplored space
        if (this.attractionPoints.length < 50) {
            this.addAttractionPoints();
        }
        
        // Update existing attraction points based on limb growth
        for (const point of this.attractionPoints) {
            if (point.reached) continue;
            
            // Check if any limb has reached this point
            for (const [coneId, cone] of this.growthCones) {
                const dx = cone.x - point.x;
                const dy = cone.y - point.y;
                const dz = cone.z - point.z;
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                
                if (dist < 15) {
                    point.reached = true;
                    point.reachedBy.push(coneId);
                }
            }
        }
        
        // Remove reached points
        this.attractionPoints = this.attractionPoints.filter(p => !p.reached);
    }
    
    private addAttractionPoints(): void {
        // Add attraction points in unexplored areas
        const nodeBounds = this.calculateNodeBounds();
        
        for (let i = 0; i < 10; i++) {
            // Find unexplored area
            const x = nodeBounds.minX + (nodeBounds.maxX - nodeBounds.minX) * Math.random();
            const y = nodeBounds.minY + (nodeBounds.maxY - nodeBounds.minY) * Math.random();
            const z = nodeBounds.minZ + (nodeBounds.maxZ - nodeBounds.minZ) * Math.random();
            
            // Calculate importance based on distance from network
            const dist = this.distanceToNearestNode(x, y, z);
            const importance = Math.exp(-dist / 100); // Higher for nearby unexplored areas
            
            this.attractionPoints.push({
                id: Date.now() + i,
                x, y, z,
                importance,
                reached: false,
                reachedBy: []
            });
        }
    }
    
    // === FRONTIER EXPLORATION ===
    
    private updateFrontierExploration(dt: number): void {
        // Identify frontiers (boundaries between explored and unexplored)
        this.frontierPoints = this.identifyFrontiers();
        
        // Update exploration values
        for (const frontier of this.frontierPoints) {
            const explored = this.explorationMap.get(`${frontier.x},${frontier.y}`) || 0;
            frontier.uncertainty = 1 - explored;
            frontier.informationGain = frontier.uncertainty * frontier.reachability;
            // Use information gain directly (no non-existent importance field)
            frontier.explorationValue = frontier.informationGain;
        }
        
        // Sort by exploration value
        this.frontierPoints.sort((a, b) => b.explorationValue - a.explorationValue);
        
        // Update exploration map as space is explored
        this.updateExploredSpace();
    }
    
    private identifyFrontiers(): FrontierPoint[] {
        const frontiers: FrontierPoint[] = [];
        const nodeBounds = this.calculateNodeBounds();
        const gridSize = 30;
        
        // Simple grid-based frontier detection
        const explored = new Set<string>();
        const frontiersFound = new Set<string>();
        
        for (const node of this.network.nodes) {
            const gx = Math.floor(node.x / gridSize);
            const gy = Math.floor(node.y / gridSize);
            explored.add(`${gx},${gy}`);
        }
        
        // Find frontiers (unexplored cells adjacent to explored cells)
        for (const node of this.network.nodes) {
            const gx = Math.floor(node.x / gridSize);
            const gy = Math.floor(node.y / gridSize);
            
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    const neighborKey = `${gx + dx},${gy + dy}`;
                    if (!explored.has(neighborKey) && !frontiersFound.has(neighborKey)) {
                        frontiersFound.add(neighborKey);
                        const x = (gx + dx) * gridSize;
                        const y = (gy + dy) * gridSize;
                        const z = 0;
                        
                        const reachability = 1 / (1 + this.distanceToNearestNode(x, y, z));
                        
                        frontiers.push({
                            id: frontiers.length,
                            x, y, z,
                            informationGain: 1.0,
                            uncertainty: 1.0,
                            reachability,
                            explorationValue: 1.0 * reachability
                        });
                    }
                }
            }
        }
        
        return frontiers;
    }
    
    private updateExploredSpace(): void {
        for (const [coneId, cone] of this.growthCones) {
            const gx = Math.floor(cone.x / 30);
            const gy = Math.floor(cone.y / 30);
            const key = `${gx},${gy}`;
            
            const currentValue = this.explorationMap.get(key) || 0;
            this.explorationMap.set(key, Math.min(1, currentValue + 0.1));
        }
    }
    
    // === HELPER METHODS ===
    
    private distanceToNearestNode(x: number, y: number, z: number): number {
        let minDist = Infinity;
        for (const node of this.network.nodes) {
            if (!node || node.corroded) continue;
            const dx = node.x - x;
            const dy = node.y - y;
            const dz = (node.z || 0) - z;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            minDist = Math.min(minDist, dist);
        }
        return minDist;
    }
    
    private calculateNodeBounds(): { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } {
        if (this.network.nodes.length === 0) {
            return { minX: 0, maxX: 800, minY: 0, maxY: 600, minZ: -50, maxZ: 50 };
        }
        
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        
        for (const node of this.network.nodes) {
            if (!node || node.corroded) continue;
            minX = Math.min(minX, node.x);
            maxX = Math.max(maxX, node.x);
            minY = Math.min(minY, node.y);
            maxY = Math.max(maxY, node.y);
            const z = node.z || 0;
            minZ = Math.min(minZ, z);
            maxZ = Math.max(maxZ, z);
        }
        
        // Add padding
        const padding = 100;
        return {
            minX: minX - padding, maxX: maxX + padding,
            minY: minY - padding, maxY: maxY + padding,
            minZ: minZ - padding, maxZ: maxZ + padding
        };
    }
    
    // === PUBLIC INTERFACE ===
    
    public createGrowthCone(x: number, y: number, z: number): number {
        const coneId = Date.now();
        this.growthCones.set(coneId, {
            id: coneId,
            x, y, z,
            direction: Math.random() * Math.PI * 2,
            explorationState: 'searching',
            velocity: 1.0,
            persistenceTime: 0,
            uncertainty: 0.7,
            attractionFactor: 0.6,
            sensedGradient: { x: 0, y: 0, z: 0 },
            gradientStrength: 0,
            lastCheckedDirection: 0,
            exploredRadius: 50,
            explorationMap: new Map()
        });
        return coneId;
    }
    
    public getGrowthCone(id: number): GrowthCone | undefined {
        return this.growthCones.get(id);
    }
    
    public getAllGrowthCones(): GrowthCone[] {
        return Array.from(this.growthCones.values());
    }
    
    public getChemotaxisFields(): ChemotaxisField[] {
        return [...this.chemotaxisFields];
    }
    
    public getAttractionPoints(): AttractionPoint[] {
        return [...this.attractionPoints];
    }
    
    public getFrontierPoints(): FrontierPoint[] {
        return [...this.frontierPoints];
    }
    
    // Get next growth direction for a limb based on exploration algorithms
    public getNextGrowthDirection(limb: Limb): { x: number; y: number; z: number } {
        const tipSegment = limb.segments[limb.segments.length - 1];
        if (!tipSegment) {
            return { x: Math.random() - 0.5, y: Math.random() - 0.5, z: 0 };
        }
        
        // Combine multiple exploration strategies
        const direction = this.combineExplorationStrategies(tipSegment);
        return direction;
    }
    
    private combineExplorationStrategies(segment: LimbSegment): { x: number; y: number; z: number } {
        let x = 0, y = 0, z = 0;
        let weightSum = 0;
        
        // Strategy 1: Chemotaxis (gradient following)
        for (const field of this.chemotaxisFields) {
            const dx = field.x - segment.x;
            const dy = field.y - segment.y;
            const dz = field.z - segment.z;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (dist < field.strength && dist > 0.01) {
                const weight = field.concentration / dist;
                const dir = field.type === 'attractant' ? 1 : -1;
                
                x += (dx / dist) * weight * dir;
                y += (dy / dist) * weight * dir;
                z += (dz / dist) * weight * dir;
                weightSum += weight;
            }
        }
        
        // Strategy 2: Space colonization (grow toward attraction points)
        if (this.attractionPoints.length > 0) {
            let nearestPoint: AttractionPoint | null = null;
            let minDist = Infinity;
            
            for (const point of this.attractionPoints) {
                if (point.reached) continue;
                const dx = point.x - segment.x;
                const dy = point.y - segment.y;
                const dz = point.z - segment.z;
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                
                if (dist < minDist) {
                    minDist = dist;
                    nearestPoint = point;
                }
            }
            
            if (nearestPoint && minDist < 200) {
                const dx = nearestPoint.x - segment.x;
                const dy = nearestPoint.y - segment.y;
                const dz = nearestPoint.z - segment.z;
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                
                const weight = nearestPoint.importance * (1 / (1 + dist / 50));
                x += (dx / dist) * weight;
                y += (dy / dist) * weight;
                z += (dz / dist) * weight;
                weightSum += weight;
            }
        }
        
        // Strategy 3: Frontier exploration (grow toward unexplored areas)
        if (this.frontierPoints.length > 0) {
            const topFrontier = this.frontierPoints[0];
            const dx = topFrontier.x - segment.x;
            const dy = topFrontier.y - segment.y;
            const dz = topFrontier.z - segment.z;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (dist > 0.01) {
                const weight = topFrontier.explorationValue * 0.5;
                x += (dx / dist) * weight;
                y += (dy / dist) * weight;
                z += (dz / dist) * weight;
                weightSum += weight;
            }
        }
        
        // Normalize
        if (weightSum > 0.01) {
            const norm = Math.sqrt(x*x + y*y + z*z);
            if (norm > 0.01) {
                return { x: x / norm, y: y / norm, z: z / norm };
            }
        }
        
        // Random exploration if no guidance
        const randomAngle = Math.random() * Math.PI * 2;
        return {
            x: Math.cos(randomAngle) * 0.5,
            y: Math.sin(randomAngle) * 0.5,
            z: (Math.random() - 0.5) * 0.3
        };
    }
}

